import { FC, useEffect, useMemo, useState } from 'react';
import { IDictionary, IOnInputError, ITestable, TError, TMediaMap, TWidths, ContentfulErrorKeys } from '@belong/types';
import { ErrorValidation, getValidationSchema } from '@belong/validation';
import { applyDictionaryToString } from '@belong/contentful';

import { ITextInput } from '../TextInput';
import { InputWrapper } from '../InputWrapper';
import { Label } from '../Label';
import { Copy } from '../../styles/Typography/Copy';
import { Error } from '../Error';
import { IUseForm } from '../useForm/useForm';
import { TextInput, TextInputWrapper } from '../TextInput/TextInput';

export interface IExtendedGenericInput<ErrorKeys extends ContentfulErrorKeys>
  extends Omit<IGenericInput, 'errorValidation' | 'name'> {
  errorMessages: Record<ErrorKeys, string>;
  name?: string;
}

export interface IExtendedGenericInputDefaultContent<ErrorKeys extends ContentfulErrorKeys>
  extends Omit<IGenericInput, 'errorValidation' | 'label' | 'name'> {
  errorMessages?: Partial<Record<ErrorKeys, string>>;
  label?: string;
  name?: string;
}

export interface IGenericInput extends Omit<ITextInput, 'name' | 'required' | 'id' | 'maxLength'>, ITestable {
  name: string;
  label: string;
  description?: string;
  dictionary?: IDictionary;
  width?: TWidths | TMediaMap;
  initialValue?: string;
  hasFormError?: boolean;
  /**
   * If there is an error related to this field outside of this component, we need to pass in the id of the error so it can be related to it
   */
  formErrorId?: string;
  manageForm: IUseForm['manageForm'];
  onInputError?: IOnInputError;
  /**
   * A list of field validations, if an empty list is provided, the field is not required and any input can be entered
   */
  errorValidation: ErrorValidation[];
}

/**
 * A generic input field. It validates based on the `errorValidation` rules. It handles form errors when implemented with the useForm hook
 *
 * Note: for a field to be required you must provide include an `errorValdiation` with the `errorKey` `required`
 */
export const GenericInput: FC<IGenericInput> = ({
  name,
  label,
  description,
  dictionary = {},
  width,
  initialValue,
  hasFormError = false,
  formErrorId = '',
  manageForm,
  onInputError,
  errorValidation,
  inputSymbolPrefix,
  'data-testid': testid = name,
  onChange,
  ...extraProps
}) => {
  const [error, setError] = useState<TError>({ hasError: hasFormError, message: '' });
  const [value, setValue] = useState<string>(initialValue ?? '');
  const idInputWrapper = `${testid}InputWrapper`;
  const idInput = `${testid}Input`;
  const idError = `${testid}Error`;
  const idLabel = `${testid}Label`;
  const idDescription = `${testid}Description`;
  const hasError = error.hasError || hasFormError;
  const describedBy =
    `${hasError ? `${idError} ${formErrorId}`.trim() : ''} ${description ? idDescription : ''}`.trim();
  const requiredValidation = useMemo(
    () => errorValidation.find(({ errorKey }) => errorKey === 'required'),
    [errorValidation]
  );

  const validateValue = async (inputValue: string | null, forceValidate?: boolean): Promise<boolean> => {
    let isValid = true;
    const valueToVerify = inputValue || '';

    if (!errorValidation.length) {
      return true;
    }

    await getValidationSchema(errorValidation)
      .validate(valueToVerify)
      .then(() => {
        setError({ hasError: false, message: '' });
        manageForm.removeInputError(name);
      })
      .catch(({ message }) => {
        manageForm.addInputError(name);
        isValid = false;
        if (error.hasError || forceValidate) {
          setError({ hasError: true, message });
        }
      });

    return isValid;
  };

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
      validateValue(initialValue, true);
    }
  }, [initialValue]);

  useEffect(() => {
    manageForm.addInputValidator(name, validateValue);

    return () => {
      manageForm.removeInputValidator(name);
    };
  }, [manageForm, name]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    setValue(inputValue);
    validateValue(inputValue);
    onChange?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setValue(inputValue);
    validateValue(inputValue, true);
  };

  useEffect(() => {
    if (onInputError && error.hasError && error.message) {
      onInputError(name, applyDictionaryToString(dictionary, error.message));
    }
  }, [onInputError, name, error]);

  useEffect(() => {
    if (requiredValidation && hasFormError && value.length === 0) {
      setError({ hasError: true, message: requiredValidation.message });
      manageForm.addInputError(name);
    }

    return () => {
      manageForm.removeInputError(name);
    };
  }, [hasFormError, requiredValidation?.message, value]);

  return (
    <InputWrapper data-testid={testid}>
      <Label id={idLabel} htmlFor={idInput}>
        {applyDictionaryToString(dictionary, label)}
      </Label>
      {description && <Copy id={idDescription}>{applyDictionaryToString(dictionary, description)}</Copy>}
      <TextInputWrapper inputSymbolPrefix={inputSymbolPrefix} data-testid={idInputWrapper}>
        <TextInput
          name={name}
          value={value}
          width={width}
          inputSymbolPrefix={inputSymbolPrefix}
          onChange={handleChange}
          onBlur={handleBlur}
          required={!!requiredValidation}
          isError={hasError}
          aria-invalid={hasError}
          aria-describedby={describedBy}
          {...extraProps}
          id={idInput}
        />
      </TextInputWrapper>
      {hasError && error.message && (
        <Error idError={idError}>{applyDictionaryToString(dictionary, error.message)}</Error>
      )}
    </InputWrapper>
  );
};

GenericInput.displayName = 'GenericInput';
